import { createServerFn } from "@tanstack/react-start";
import {
  and,
  count,
  desc,
  eq,
  gte,
  inArray,
  isNotNull,
  isNull,
  lt,
  ne,
  or,
  sql,
} from "drizzle-orm";
import { z } from "zod";
import { db } from "~/lib/db";
import {
  importBatches,
  projectCitations,
  projectInvitation,
  projectMember,
  projectScreeningSettings,
  screeningDecisions,
  user,
} from "~/lib/db/schema";
import { requireProjectAccess, requireUser } from "~/lib/server/helpers.server";

const projectIdSchema = z.object({
  projectId: z.string(),
});

export interface ProjectMetrics {
  totalCitations: number;
  included: number;
  excluded: number;
  conflicts: number;
  pending: number;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  image: string | null;
  role: "owner" | "reviewer" | "viewer";
  screened: number;
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: "reviewer" | "viewer";
  expiresAt: Date;
}

export interface RecentImport {
  id: string;
  filename: string | null;
  sourceName: string | null;
  recordsImported: number;
  recordsTotal: number;
  status: string | null;
  createdAt: Date;
  importedBy: {
    name: string;
    image: string | null;
  };
}

export interface UserProgress {
  screened: number;
  remaining: number;
}

export interface ProjectOverviewData {
  metrics: ProjectMetrics;
  team: TeamMember[];
  pendingInvitations: PendingInvitation[];
  recentImports: RecentImport[];
  userProgress: UserProgress;
}

export const $getProjectOverview = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => projectIdSchema.parse(data))
  .handler(async ({ data }): Promise<ProjectOverviewData> => {
    const currentUser = await requireUser();
    const projectAccess = await requireProjectAccess(data.projectId, currentUser.id);

    // Fetch all data in parallel
    const [
      metricsResult,
      teamResult,
      invitationsResult,
      importsResult,
      userProgressResult,
    ] = await Promise.all([
      getProjectMetrics(data.projectId),
      getTeamMembers(data.projectId, projectAccess.project.userId),
      getPendingInvitations(data.projectId),
      getRecentImports(data.projectId),
      getUserProgress(data.projectId, currentUser.id),
    ]);

    return {
      metrics: metricsResult,
      team: teamResult,
      pendingInvitations: invitationsResult,
      recentImports: importsResult,
      userProgress: userProgressResult,
    };
  });

async function getProjectMetrics(projectId: string): Promise<ProjectMetrics> {
  // Get citations that are not confirmed duplicates
  const result = await db
    .select({
      screeningStatus: projectCitations.screeningStatus,
      count: count(),
    })
    .from(projectCitations)
    .where(
      and(
        eq(projectCitations.projectId, projectId),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          sql`${projectCitations.duplicateStatus} IS NULL`,
        ),
      ),
    )
    .groupBy(projectCitations.screeningStatus);

  const metrics: ProjectMetrics = {
    totalCitations: 0,
    included: 0,
    excluded: 0,
    conflicts: 0,
    pending: 0,
  };

  for (const row of result) {
    const statusCount = row.count;
    metrics.totalCitations += statusCount;

    switch (row.screeningStatus) {
      case "pending":
        metrics.pending = statusCount;
        break;
      case "included":
        metrics.included = statusCount;
        break;
      case "excluded":
        metrics.excluded = statusCount;
        break;
      case "maybe":
        // Count "maybe" as pending since they need a decision
        metrics.pending += statusCount;
        break;
    }
  }

  // Get pending duplicate groups as conflicts
  const conflictsResult = await db
    .selectDistinct({ groupId: projectCitations.duplicateGroupId })
    .from(projectCitations)
    .where(
      and(
        eq(projectCitations.projectId, projectId),
        eq(projectCitations.duplicateStatus, "potential_duplicate"),
        isNotNull(projectCitations.duplicateGroupId),
      ),
    );

  metrics.conflicts = conflictsResult.length;

  return metrics;
}

async function getTeamMembers(projectId: string, ownerId: string): Promise<TeamMember[]> {
  // Get owner info
  const owner = await db.query.user.findFirst({
    where: eq(user.id, ownerId),
  });

  const members: TeamMember[] = [];

  if (owner) {
    // Get owner's screening count
    const ownerScreened = await db
      .select({ count: count() })
      .from(projectCitations)
      .where(
        and(
          eq(projectCitations.projectId, projectId),
          eq(projectCitations.screenedBy, owner.id),
        ),
      );

    members.push({
      id: owner.id,
      name: owner.name,
      email: owner.email,
      image: owner.image,
      role: "owner",
      screened: ownerScreened[0]?.count ?? 0,
    });
  }

  // Get project members
  const projectMembers = await db.query.projectMember.findMany({
    where: eq(projectMember.projectId, projectId),
    with: {
      user: true,
    },
  });

  for (const member of projectMembers) {
    const memberScreened = await db
      .select({ count: count() })
      .from(projectCitations)
      .where(
        and(
          eq(projectCitations.projectId, projectId),
          eq(projectCitations.screenedBy, member.user.id),
        ),
      );

    members.push({
      id: member.user.id,
      name: member.user.name,
      email: member.user.email,
      image: member.user.image,
      role: member.role as "reviewer" | "viewer",
      screened: memberScreened[0]?.count ?? 0,
    });
  }

  return members;
}

async function getPendingInvitations(projectId: string): Promise<PendingInvitation[]> {
  const now = new Date();

  await db
    .update(projectInvitation)
    .set({ status: "expired" })
    .where(
      and(
        eq(projectInvitation.projectId, projectId),
        eq(projectInvitation.status, "pending"),
        lt(projectInvitation.expiresAt, now),
      ),
    );

  const invitations = await db.query.projectInvitation.findMany({
    where: and(
      eq(projectInvitation.projectId, projectId),
      eq(projectInvitation.status, "pending"),
      gte(projectInvitation.expiresAt, now),
    ),
  });

  return invitations.map((inv) => ({
    id: inv.id,
    email: inv.email,
    role: inv.role as "reviewer" | "viewer",
    expiresAt: inv.expiresAt,
  }));
}

async function getRecentImports(projectId: string): Promise<RecentImport[]> {
  const imports = await db.query.importBatches.findMany({
    where: eq(importBatches.projectId, projectId),
    orderBy: desc(importBatches.createdAt),
    limit: 5,
    with: {
      importedByUser: true,
    },
  });

  return imports.map((imp) => ({
    id: imp.id,
    filename: imp.filename,
    sourceName: imp.sourceName,
    recordsImported: imp.recordsImported ?? 0,
    recordsTotal: imp.recordsTotal ?? 0,
    status: imp.status,
    createdAt: imp.createdAt,
    importedBy: {
      name: imp.importedByUser.name,
      image: imp.importedByUser.image,
    },
  }));
}

async function getUserProgress(projectId: string, userId: string): Promise<UserProgress> {
  // Get screening settings to determine mode
  const settings = await db.query.projectScreeningSettings.findFirst({
    where: eq(projectScreeningSettings.projectId, projectId),
  });

  const isDualMode = settings?.screeningMode === "dual";

  if (isDualMode) {
    // Dual mode: count user's decisions from screeningDecisions table
    const userDecisions = await db
      .select({ count: count() })
      .from(screeningDecisions)
      .innerJoin(
        projectCitations,
        eq(screeningDecisions.projectCitationId, projectCitations.id),
      )
      .where(
        and(
          eq(projectCitations.projectId, projectId),
          eq(screeningDecisions.reviewerId, userId),
          isNull(screeningDecisions.deletedAt),
        ),
      );

    const screened = userDecisions[0]?.count ?? 0;

    // Count user's remaining queue (pending/awaiting_second not yet reviewed by user)
    // First get all pending/awaiting_second citations
    const pendingCitations = await db
      .select({ id: projectCitations.id })
      .from(projectCitations)
      .where(
        and(
          eq(projectCitations.projectId, projectId),
          inArray(projectCitations.consensusStatus, ["pending", "awaiting_second"]),
          or(
            ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
            isNull(projectCitations.duplicateStatus),
          ),
        ),
      );

    if (pendingCitations.length === 0) {
      return { screened, remaining: 0 };
    }

    // Get citations already reviewed by this user
    const citationIds = pendingCitations.map((c) => c.id);
    const reviewedByUser = await db
      .select({ citationId: screeningDecisions.projectCitationId })
      .from(screeningDecisions)
      .where(
        and(
          inArray(screeningDecisions.projectCitationId, citationIds),
          eq(screeningDecisions.reviewerId, userId),
          isNull(screeningDecisions.deletedAt),
        ),
      );

    const reviewedIds = new Set(reviewedByUser.map((r) => r.citationId));
    const remaining = pendingCitations.filter((c) => !reviewedIds.has(c.id)).length;

    return { screened, remaining };
  }

  // Single mode: existing logic
  const screenedResult = await db
    .select({ count: count() })
    .from(projectCitations)
    .where(
      and(
        eq(projectCitations.projectId, projectId),
        eq(projectCitations.screenedBy, userId),
      ),
    );

  const screened = screenedResult[0]?.count ?? 0;

  // Count total pending citations
  const pendingResult = await db
    .select({ count: count() })
    .from(projectCitations)
    .where(
      and(
        eq(projectCitations.projectId, projectId),
        eq(projectCitations.screeningStatus, "pending"),
        or(
          ne(projectCitations.duplicateStatus, "confirmed_duplicate"),
          isNull(projectCitations.duplicateStatus),
        ),
      ),
    );

  const remaining = pendingResult[0]?.count ?? 0;

  return { screened, remaining };
}
